import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Row, Col, Button, Table, Container } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';
import getResponseErrors from '../../../../utils/getResponseErrors';
import getValidationErrors from '../../../../utils/getValidationErrors';
import { formatPrice } from '../../../../utils/funcoes';
import { ICartaoSaude } from '../../../../pages/CartaoSaudeDetalhe';

import Input from '../../../Input';
import InputMask from '../../../InputMask';
import Loading from '../../../Loading';

interface IFormChip {
  ChiNumero: string;
  ChiTelefone: string;
}

interface ICreateChipDTO {
  PlaCodigo: number;
  updated_at: Date;
  chips: IFormChip[];
}

interface IReturnSimulacao {
  PlaValor: number;
  PlaValorFormatado: string;
  parcelas: {
    CprValor: number;
    CprValorFormatado: string;
    CprDataVenc: Date;
    CprHistorico: string;
  }[];
  geraNovoContrato: boolean;
}

type Props = {
  plaCodigo: number;
  updated_at: Date;
  handleFecharComponent(dados: ICartaoSaude): void;
};
const ChipCadastro: React.FC<Props> = ({
  updated_at,
  plaCodigo,
  handleFecharComponent,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [chipsAdicionar, setChipsAdicionar] = useState<IFormChip[]>();
  const [dadosSimulados, setDadosSimulados] = useState<IReturnSimulacao>();
  const formRef = useRef<FormHandles>(null);

  useEffect(() => {
    async function loadDadosAdd(): Promise<void> {
      setLoading(true);
      try {
        if (chipsAdicionar) {
          const response = await api.post<IReturnSimulacao>(
            'planos/chipsSimula',
            {
              PlaCodigo: plaCodigo,
              chips: chipsAdicionar,
              updated_at,
            },
          );

          setDadosSimulados({
            ...response.data,
            PlaValorFormatado: formatPrice(response.data.PlaValor),
            parcelas: response.data.parcelas?.map(par => {
              return {
                ...par,
                CprValorFormatado: formatPrice(par.CprValor),
              };
            }),
          });
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível simular adição de dependentes',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (chipsAdicionar && chipsAdicionar.length > 0) loadDadosAdd();
  }, [chipsAdicionar, plaCodigo, updated_at, addToast]);

  const handleSubmitAddChip = useCallback(async (dados: IFormChip) => {
    formRef.current?.setErrors({});
    try {
      formRef.current?.setErrors({});
      const schema = Yup.object().shape({
        ChiNumero: Yup.string(),
        ChiTelefone: Yup.string().required(),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setChipsAdicionar(state => {
        return state
          ? [
              ...state,
              {
                ChiNumero: dados.ChiNumero,
                ChiTelefone: dados.ChiTelefone,
              },
            ]
          : [
              {
                ChiNumero: dados.ChiNumero,
                ChiTelefone: dados.ChiTelefone,
              },
            ];
      });

      formRef.current?.setFieldValue('ChiNumero', '');
      formRef.current?.setFieldValue('ChiTelefone', '');
      formRef.current?.reset();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRef.current?.setErrors(errors);
      }
    }
  }, []);

  const handleSubmitChips = useCallback(async () => {
    setLoading(true);
    try {
      if (chipsAdicionar) {
        const postChip: ICreateChipDTO = {
          PlaCodigo: plaCodigo,
          updated_at,
          chips: chipsAdicionar,
        };

        const response = await api.post(`planos/addchips`, postChip);

        handleFecharComponent(response.data);
      }
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível salvar comentário',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [plaCodigo, updated_at, chipsAdicionar, handleFecharComponent, addToast]);

  const handleRemoveChip = useCallback((ChiNumero: string) => {
    setChipsAdicionar(state =>
      state ? state.filter(chi => chi.ChiNumero !== ChiNumero) : undefined,
    );
  }, []);

  return (
    <Container style={{ height: '100%', minHeight: '300px' }}>
      {loading && <Loading />}

      <Form ref={formRef} className="mt-2 pb-5" onSubmit={handleSubmitAddChip}>
        <Row>
          <Col md={4}>
            <Input label="Identificador do Chip" name="ChiNumero" />
          </Col>
          <Col md={4}>
            <InputMask
              name="ChiTelefone"
              label="Numero Linha"
              mask="(99) 99999-9999"
              placeholder="(00) 00000-0000"
            />
          </Col>
          <Col sm={2}>
            <Button
              type="submit"
              size="sm"
              variant="primary"
              style={{ marginTop: '19px' }}
            >
              Adicionar
            </Button>
          </Col>
        </Row>

        {chipsAdicionar && chipsAdicionar.length > 0 && (
          <div style={{ fontSize: '.8rem' }}>
            <Row className="mt-3">
              <Col sm={5}>
                <b>Indetificador</b>
              </Col>
              <Col sm={5}>
                <b>Numero Linha</b>
              </Col>

              <Col />
            </Row>
            {chipsAdicionar.map(chi => (
              <Row key={`chi-add-${chi.ChiNumero}`} className="border-bottom">
                <Col sm={5} className="py-1">
                  {chi.ChiNumero}
                </Col>
                <Col sm={5} className="py-1">
                  {chi.ChiTelefone}
                </Col>
                <Col sm={2}>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleRemoveChip(chi.ChiNumero)}
                  >
                    <FaTrash size={14} />
                  </Button>
                </Col>
              </Row>
            ))}

            <div className="mt-2 py-2">
              {dadosSimulados && (
                <>
                  <p style={{ fontSize: '.9rem' }}>
                    O valor do plano sera:{' '}
                    <b>{dadosSimulados.PlaValorFormatado}</b>. Parcela
                    adicional:
                    <br />
                    <Table className="text-secondary" hover size="sm">
                      <thead>
                        <tr className="bg-light text-dark">
                          <th>Parcela</th>
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dadosSimulados.parcelas.map(par => (
                          <tr key={`chip-${par.CprHistorico}`}>
                            <td>{par.CprHistorico}</td>
                            <td>{par.CprValorFormatado}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {dadosSimulados.geraNovoContrato && (
                      <div className="alert alert-warning px-3 py-1 mt-2">
                        <h5>Atenção!</h5>
                        Essa operação írá gerar um novo contrato de aquisição de
                        chip.
                      </div>
                    )}
                  </p>
                  <Button
                    type="button"
                    className="mt-2"
                    variant="success"
                    onClick={handleSubmitChips}
                    disabled={loading}
                  >
                    Salvar
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Form>
    </Container>
  );
};

export default ChipCadastro;
